import { useState, useEffect, useMemo } from 'react';
import { Receipt, AlertCircle, Download, FileSpreadsheet, Calendar } from 'lucide-react';
import { ExpandToggle } from '../ui/ListRows';
import { useCreatorPortal } from '../../contexts/CreatorPortalContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { formatEuro } from '../../utils/currency';
import { getCreatorQuarterlyTaxData, downloadCSV, type QuarterlyTaxData } from '../../services/creatorPortalService';

const IRC_RATE = 0.25;

const QUARTER_COLORS = [
  { bg: 'bg-emerald-50', border: 'border-emerald-100', text: 'text-emerald-700', bar: 'bg-emerald-500' },
  { bg: 'bg-teal-50', border: 'border-teal-100', text: 'text-teal-700', bar: 'bg-teal-500' },
  { bg: 'bg-amber-50', border: 'border-amber-100', text: 'text-amber-700', bar: 'bg-amber-500' },
  { bg: 'bg-rose-50', border: 'border-rose-100', text: 'text-rose-700', bar: 'bg-rose-500' },
];

const IVA_DEADLINES = [
  { quarter: 1, deadline: '15 May', deadlinePT: '15 Mai' },
  { quarter: 2, deadline: '15 Aug', deadlinePT: '15 Ago' },
  { quarter: 3, deadline: '15 Nov', deadlinePT: '15 Nov' },
  { quarter: 4, deadline: '15 Feb', deadlinePT: '15 Fev' },
];

function getQuarterLabel(quarter: number, t: (k: string) => string) {
  const keys = ['creatorsHub.tax.q1', 'creatorsHub.tax.q2', 'creatorsHub.tax.q3', 'creatorsHub.tax.q4'];
  return t(keys[quarter - 1] || keys[0]);
}

export function CreatorTaxPlanner() {
  const { t } = useLanguage();
  const { campaigns, influencer } = useCreatorPortal();
  const [data, setData] = useState<QuarterlyTaxData[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedQuarter, setExpandedQuarter] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());

  useEffect(() => {
    const campaignIds = campaigns.map(c => c.id);
    if (campaignIds.length === 0) {
      setLoading(false);
      return;
    }
    setLoading(true);
    getCreatorQuarterlyTaxData(campaignIds, campaigns)
      .then(setData)
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [campaigns]);

  const availableYears = useMemo(() => {
    const years = [...new Set(data.map(d => d.year))].sort((a, b) => b - a);
    if (years.length === 0) years.push(new Date().getFullYear());
    return years;
  }, [data]);

  useEffect(() => {
    if (availableYears.length > 0 && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  const yearData = useMemo(() => data.filter(d => d.year === selectedYear), [data, selectedYear]);

  const annualTotals = useMemo(() => {
    return yearData.reduce(
      (acc, q) => ({
        invoiceCount: acc.invoiceCount + q.invoiceCount,
        totalLiquido: acc.totalLiquido + q.totalLiquido,
        totalIVA: acc.totalIVA + q.totalIVA,
        totalBruto: acc.totalBruto + q.totalBruto,
      }),
      { invoiceCount: 0, totalLiquido: 0, totalIVA: 0, totalBruto: 0 }
    );
  }, [yearData]);

  const maxQuarterBruto = useMemo(
    () => Math.max(...yearData.map(q => q.totalBruto), 1),
    [yearData]
  );

  const estimatedIRC = annualTotals.totalLiquido * IRC_RATE;
  const effectiveTaxRate = annualTotals.totalBruto > 0
    ? ((annualTotals.totalIVA + estimatedIRC) / annualTotals.totalBruto * 100)
    : 0;

  const handleExportCSV = () => {
    const name = influencer?.name || 'Creator';
    const headers = [
      t('creatorsHub.tax.quarter'),
      t('creatorsHub.tax.invoices'),
      `${t('creatorsHub.tax.baseTributavel')} (EUR)`,
      `${t('creatorsHub.tax.totalIVA')} (23%) (EUR)`,
      `${t('creatorsHub.tax.totalBruto')} (EUR)`,
      t('creatorsHub.tax.ivaDeadline'),
    ];

    const rows: string[][] = [];
    for (let q = 1; q <= 4; q++) {
      const qData = yearData.find(d => d.quarter === q);
      const deadline = IVA_DEADLINES[q - 1];
      rows.push([
        getQuarterLabel(q, t),
        String(qData?.invoiceCount || 0),
        (qData?.totalLiquido || 0).toFixed(2),
        (qData?.totalIVA || 0).toFixed(2),
        (qData?.totalBruto || 0).toFixed(2),
        deadline.deadline,
      ]);
    }

    rows.push([]);
    rows.push([
      t('creatorsHub.tax.annualTotal'),
      String(annualTotals.invoiceCount),
      annualTotals.totalLiquido.toFixed(2),
      annualTotals.totalIVA.toFixed(2),
      annualTotals.totalBruto.toFixed(2),
      '',
    ]);
    rows.push([]);
    rows.push([t('creatorsHub.tax.plSummary'), '', '', '', '', '']);
    rows.push([t('creatorsHub.tax.grossRevenue'), '', '', '', annualTotals.totalBruto.toFixed(2), '']);
    rows.push([`${t('creatorsHub.tax.totalIVA')} (23%)`, '', '', '', `- ${annualTotals.totalIVA.toFixed(2)}`, '']);
    rows.push([t('creatorsHub.tax.baseTributavel'), '', '', '', annualTotals.totalLiquido.toFixed(2), '']);
    rows.push([`${t('creatorsHub.tax.estimatedIRC')} (25%)`, '', '', '', `- ${estimatedIRC.toFixed(2)}`, '']);
    rows.push([t('creatorsHub.tax.netAfterTax'), '', '', '', (annualTotals.totalLiquido - estimatedIRC).toFixed(2), '']);

    const csvContent = [headers, ...rows]
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');

    downloadCSV(csvContent, `${name}_Tax_Report_${selectedYear}.csv`);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl ring-1 ring-gray-100 shadow-sm p-12 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="bg-white rounded-2xl ring-1 ring-gray-100 shadow-sm p-12 text-center">
        <Receipt className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500 font-medium">{t('creatorsHub.tax.noInvoices')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{t('creatorsHub.tax.title')}</h2>
          <p className="text-sm text-gray-500">{t('creatorsHub.tax.subtitle')}</p>
        </div>
        <div className="flex items-center gap-3">
          {availableYears.length > 1 && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 uppercase tracking-wider">{t('creatorsHub.tax.yearLabel')}</span>
              <select
                value={selectedYear}
                onChange={e => setSelectedYear(Number(e.target.value))}
                className="text-sm font-medium bg-white border border-gray-200 rounded-xl px-3 py-1.5 focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500 focus:outline-none"
              >
                {availableYears.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          )}
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Download className="w-3.5 h-3.5" />
            {t('creatorsHub.tax.export')}
          </button>
        </div>
      </div>

      {/* P&L Summary Cards */}
      <div className="bg-white rounded-2xl ring-1 ring-gray-100 shadow-sm overflow-hidden">
        <div className="p-5 border-b border-gray-100">
          <div className="flex items-center gap-2 mb-4">
            <FileSpreadsheet className="w-4 h-4 text-gray-500" />
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider">{t('creatorsHub.tax.plSummary')}</h3>
            <span className="text-xs text-gray-400">{selectedYear}</span>
          </div>

          <div className="space-y-0">
            <PLRow label={t('creatorsHub.tax.grossRevenue')} value={annualTotals.totalBruto} bold />
            <PLRow label={`  ${t('creatorsHub.tax.totalIVA')} (23%)`} value={-annualTotals.totalIVA} color="text-amber-600" />
            <div className="border-t border-gray-200 my-2" />
            <PLRow label={t('creatorsHub.tax.baseTributavel')} value={annualTotals.totalLiquido} bold color="text-emerald-600" />
            <PLRow label={`  ${t('creatorsHub.tax.estimatedIRC')} (25%)`} value={-estimatedIRC} color="text-rose-600" />
            <div className="border-t border-gray-200 my-2" />
            <PLRow label={t('creatorsHub.tax.netAfterTax')} value={annualTotals.totalLiquido - estimatedIRC} bold color="text-blue-600" size="lg" />
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-gray-100 bg-gray-50">
          <MiniStat label={t('creatorsHub.tax.totalBruto')} value={formatEuro(annualTotals.totalBruto)} />
          <MiniStat label={t('creatorsHub.tax.totalIVA')} value={formatEuro(annualTotals.totalIVA)} valueColor="text-amber-600" />
          <MiniStat label={t('creatorsHub.tax.estimatedIRC')} value={formatEuro(estimatedIRC)} valueColor="text-rose-600" />
          <MiniStat label={t('creatorsHub.tax.effectiveRate')} value={`${effectiveTaxRate.toFixed(1)}%`} />
        </div>
      </div>

      {/* Quarterly Breakdown */}
      <div className="bg-white rounded-2xl ring-1 ring-gray-100 shadow-sm overflow-hidden">
        <div className="hidden sm:grid grid-cols-[1fr_70px_110px_110px_110px_100px] gap-3 px-5 py-3 bg-gray-50 border-b border-gray-100 text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
          <span>{t('creatorsHub.tax.quarter')}</span>
          <span className="text-center">{t('creatorsHub.tax.invoices')}</span>
          <span className="text-right">{t('creatorsHub.tax.baseTributavel')}</span>
          <span className="text-right">{t('creatorsHub.tax.totalIVA')}</span>
          <span className="text-right">{t('creatorsHub.tax.totalBruto')}</span>
          <span className="text-right">{t('creatorsHub.tax.ivaDeadline')}</span>
        </div>

        <div className="divide-y divide-gray-50">
          {[1, 2, 3, 4].map(q => {
            const qData = yearData.find(d => d.quarter === q);
            const colors = QUARTER_COLORS[q - 1];
            const key = `${selectedYear}-Q${q}`;
            const isExpanded = expandedQuarter === key;
            const barWidth = qData ? (qData.totalBruto / maxQuarterBruto) * 100 : 0;
            const deadline = IVA_DEADLINES[q - 1];

            return (
              <div key={q}>
                <button
                  onClick={() => setExpandedQuarter(isExpanded ? null : key)}
                  aria-expanded={isExpanded}
                  className={`group w-full text-left px-5 py-4 transition-colors sm:grid sm:grid-cols-[1fr_70px_110px_110px_110px_100px] sm:gap-3 sm:items-center ${isExpanded ? 'bg-gray-50' : 'hover:bg-gray-50'}`}
                >
                  <div className="flex items-center gap-3">
                    <ExpandToggle open={isExpanded} />
                    <div className={`w-8 h-8 rounded-lg ${colors.bg} flex items-center justify-center`}>
                      <span className={`text-xs font-bold ${colors.text}`}>Q{q}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {getQuarterLabel(q, t)}
                      </p>
                      <div className="mt-1 w-full max-w-[160px] h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-700 ${colors.bar}`}
                          style={{ width: `${barWidth}%` }}
                        />
                      </div>
                    </div>
                  </div>

                  <p className="text-sm text-center font-medium text-gray-700 hidden sm:block">
                    {qData?.invoiceCount || 0}
                  </p>
                  <p className="text-sm text-right font-semibold text-gray-900 hidden sm:block">
                    {formatEuro(qData?.totalLiquido || 0)}
                  </p>
                  <p className="text-sm text-right font-medium text-amber-600 hidden sm:block">
                    {formatEuro(qData?.totalIVA || 0)}
                  </p>
                  <p className="text-sm text-right font-semibold text-gray-900 hidden sm:block">
                    {formatEuro(qData?.totalBruto || 0)}
                  </p>
                  <div className="hidden sm:flex items-center justify-end gap-1 text-xs text-gray-500">
                    <Calendar className="w-3 h-3" />
                    <span>{deadline.deadline}</span>
                  </div>
                </button>

                {isExpanded && (
                  <div className="px-5 pb-4 animate-in fade-in slide-in-from-top-1 duration-200">
                    <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                      {qData ? (
                        <>
                          <div className="grid grid-cols-2 gap-3 sm:hidden">
                            <DetailCell label={t('creatorsHub.tax.invoices')} value={String(qData.invoiceCount)} />
                            <DetailCell label={t('creatorsHub.tax.baseTributavel')} value={formatEuro(qData.totalLiquido)} />
                            <DetailCell label={t('creatorsHub.tax.totalIVA')} value={formatEuro(qData.totalIVA)} valueColor="text-amber-600" />
                            <DetailCell label={t('creatorsHub.tax.totalBruto')} value={formatEuro(qData.totalBruto)} bold />
                          </div>
                          <div className="space-y-1.5">
                            <PLRow label={t('creatorsHub.tax.grossRevenue')} value={qData.totalBruto} small />
                            <PLRow label={`  IVA (23%)`} value={-qData.totalIVA} color="text-amber-600" small />
                            <div className="border-t border-gray-200 my-1" />
                            <PLRow label={t('creatorsHub.tax.baseTributavel')} value={qData.totalLiquido} bold small color="text-emerald-600" />
                            <PLRow label={`  IRC (25%)`} value={-(qData.totalLiquido * IRC_RATE)} color="text-rose-600" small />
                            <div className="border-t border-gray-200 my-1" />
                            <PLRow label={t('creatorsHub.tax.netAfterTax')} value={qData.totalLiquido * (1 - IRC_RATE)} bold small color="text-blue-600" />
                          </div>
                          <div className="flex items-center gap-1.5 pt-1 text-xs text-gray-500">
                            <Calendar className="w-3 h-3" />
                            <span>{t('creatorsHub.tax.ivaDeadline')}: <strong>{deadline.deadline}</strong></span>
                          </div>
                        </>
                      ) : (
                        <p className="text-sm text-gray-400 text-center py-2">{t('creatorsHub.tax.noDataQuarter')}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="hidden sm:grid grid-cols-[1fr_70px_110px_110px_110px_100px] gap-3 px-5 py-4 bg-gray-50 border-t border-gray-200">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-gray-900">{t('creatorsHub.tax.annualTotal')}</span>
            <span className="text-xs text-gray-400">{selectedYear}</span>
          </div>
          <p className="text-sm text-center font-bold text-gray-900">{annualTotals.invoiceCount}</p>
          <p className="text-sm text-right font-bold text-emerald-600">{formatEuro(annualTotals.totalLiquido)}</p>
          <p className="text-sm text-right font-bold text-amber-600">{formatEuro(annualTotals.totalIVA)}</p>
          <p className="text-sm text-right font-bold text-gray-900">{formatEuro(annualTotals.totalBruto)}</p>
          <span />
        </div>

        <div className="sm:hidden px-5 py-4 bg-gray-50 border-t border-gray-200">
          <div className="grid grid-cols-2 gap-3">
            <DetailCell label={t('creatorsHub.tax.baseTributavel')} value={formatEuro(annualTotals.totalLiquido)} bold valueColor="text-emerald-600" />
            <DetailCell label={t('creatorsHub.tax.totalIVA')} value={formatEuro(annualTotals.totalIVA)} valueColor="text-amber-600" />
            <DetailCell label={t('creatorsHub.tax.totalBruto')} value={formatEuro(annualTotals.totalBruto)} bold />
            <DetailCell label={t('creatorsHub.tax.invoices')} value={String(annualTotals.invoiceCount)} />
          </div>
        </div>
      </div>

      {/* Tax Advisory Note */}
      <div className="bg-gradient-to-r from-slate-50 to-gray-50 rounded-xl border border-gray-200 p-5">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center shrink-0">
            <AlertCircle className="w-5 h-5 text-slate-600" />
          </div>
          <div className="flex-1 space-y-3">
            <div className="flex items-baseline justify-between">
              <h4 className="text-sm font-semibold text-gray-900">{t('creatorsHub.tax.estimatedIRC')}</h4>
              <span className="text-lg font-bold text-slate-700">{formatEuro(estimatedIRC)}</span>
            </div>
            <p className="text-xs text-gray-500">{t('creatorsHub.tax.ircNote')}</p>
            <p className="text-[10px] text-gray-400 italic">{t('creatorsHub.tax.disclaimer')}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function PLRow({
  label, value, bold, color, small, size,
}: {
  label: string;
  value: number;
  bold?: boolean;
  color?: string;
  small?: boolean;
  size?: 'lg';
}) {
  const textSize = size === 'lg' ? 'text-base' : small ? 'text-xs' : 'text-sm';
  const valueColor = color || 'text-gray-900';

  return (
    <div className={`flex justify-between items-baseline py-0.5 ${textSize}`}>
      <span className={`${bold ? 'font-semibold text-gray-900' : 'text-gray-600'}`}>{label}</span>
      <span className={`${bold ? 'font-bold' : 'font-medium'} ${valueColor} tabular-nums`}>
        {value < 0 ? `- ${formatEuro(Math.abs(value))}` : formatEuro(value)}
      </span>
    </div>
  );
}

function MiniStat({ label, value, valueColor }: { label: string; value: string; valueColor?: string }) {
  return (
    <div className="px-4 py-3 text-center">
      <p className="text-[10px] font-medium text-gray-500 uppercase tracking-wider mb-0.5">{label}</p>
      <p className={`text-sm font-bold ${valueColor || 'text-gray-900'} tabular-nums`}>{value}</p>
    </div>
  );
}

function DetailCell({ label, value, bold, valueColor }: { label: string; value: string; bold?: boolean; valueColor?: string }) {
  return (
    <div>
      <p className="text-[10px] text-gray-500 uppercase tracking-wider">{label}</p>
      <p className={`text-sm ${bold ? 'font-bold' : 'font-medium'} ${valueColor || 'text-gray-900'} tabular-nums`}>{value}</p>
    </div>
  );
}
